import React, { useState } from 'react';
import { X, ChevronRight, ChevronLeft, Check, MessageSquare, Zap, Users, Eye } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface OnboardingStep {
  icon: React.ReactNode;
  title: string;
  description: string;
  image?: string;
  tips: string[];
}

interface SimpleOnboardingProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

/**
 * Simple onboarding tutorial for new users
 * Uses clear language and visual guides
 */
export function SimpleOnboarding({ isOpen, onClose, onComplete }: SimpleOnboardingProps) {
  const [currentStep, setCurrentStep] = useState(0);

  const steps: OnboardingStep[] = [
    {
      icon: <MessageSquare className="w-16 h-16 text-green-600" />,
      title: 'Welcome to Sentlo! 👋',
      description: 'Sentlo helps you share and see what\'s happening in your area. Traffic, power outages, events - your neighbors share it all here.',
      tips: [
        'See real-time updates from people near you',
        'Share updates to help others',
        'Stay informed about your community'
      ]
    },
    {
      icon: <Eye className="w-16 h-16 text-blue-600" />,
      title: 'How to View Updates 👀',
      description: 'Scroll down to see what people are sharing. Look for recent posts about traffic, power, and local events.',
      tips: [
        'Newest updates appear at the top',
        'Green badges show verified information',
        'Tap "Verify" if you see the same thing'
      ]
    },
    {
      icon: <Zap className="w-16 h-16 text-amber-600" />,
      title: 'How to Report 📢',
      description: 'Tap the big green "REPORT NOW" button to share what you see. It\'s like sending a message to your neighbors.',
      tips: [
        'Choose what you want to report (traffic, power, etc.)',
        'Write a short message',
        'Press "Submit" - that\'s it!'
      ]
    },
    {
      icon: <Users className="w-16 h-16 text-purple-600" />,
      title: 'Help Your Community 🤝',
      description: 'When you verify or share updates, you help everyone. The more people use Sentlo, the better it works for everyone.',
      tips: [
        'Only share what you actually see',
        'Be helpful and kind',
        'Check Sentlo before traveling'
      ]
    }
  ];

  const step = steps[currentStep];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('sentlo_has_seen_onboarding', 'true');
    onComplete();
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem('sentlo_has_seen_onboarding', 'true');
    onComplete();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-gray-500">
                Step {currentStep + 1} of {steps.length}
              </span>
            </div>
            <button
              onClick={handleSkip}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="px-6 pt-4">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-green-500 to-green-600"
                initial={{ width: 0 }}
                animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="p-8 space-y-6">
            {/* Icon */}
            <div className="flex justify-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
                {step.icon}
              </div>
            </div>

            {/* Title */}
            <h2 className="text-3xl font-black text-gray-900 text-center">
              {step.title}
            </h2>

            {/* Description */}
            <p className="text-xl leading-relaxed text-gray-700 text-center">
              {step.description}
            </p>

            {/* Tips */}
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 space-y-3">
              <h3 className="text-lg font-bold text-green-900 flex items-center gap-2">
                <Check className="w-5 h-5" />
                Quick Tips:
              </h3>
              <ul className="space-y-2">
                {step.tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center flex-shrink-0 mt-0.5 text-sm font-bold">
                      {index + 1}
                    </div>
                    <span className="text-base text-green-900 leading-relaxed">
                      {tip}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Footer - Navigation */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-5 flex items-center justify-between">
            <button
              onClick={handleBack}
              disabled={currentStep === 0}
              className="flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:pointer-events-none transition-all"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>

            <div className="flex gap-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    index === currentStep
                      ? 'bg-green-600 w-8'
                      : index < currentStep
                      ? 'bg-green-400'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 shadow-lg shadow-green-600/30 transition-all"
            >
              {currentStep === steps.length - 1 ? (
                <>
                  Get Started
                  <Check className="w-5 h-5" />
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}